import React from "react";
import "./DateInput.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { SelectField } from "../SelectField";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const parseDateValue = (rawValue = "") => {
  if (!rawValue) return { day: "", month: "", year: "" };

  const str = String(rawValue).trim();
  if (str.startsWith("--")) {
    const [month = "", day = ""] = str.slice(2).split("-");
    return { day: day || "", month: month || "", year: "" };
  }

  const parts = str.split("-");
  if (parts.length === 3) {
    const [year = "", month = "", day = ""] = parts;
    return {
      day: day || "",
      month: month || "",
      year: year === "1904" ? "" : year || "",
    };
  }

  if (parts.length === 2) {
    const [month = "", day = ""] = parts;
    return { day: day || "", month: month || "", year: "" };
  }

  return { day: "", month: "", year: "" };
};

const combineDateValue = ({ day, month, year }, showYear = false) => {
  if (showYear) {
    if (!day && !month && !year) return "";
    if (day && month && year) return `${year}-${month}-${day}`;
    if (day && month && !year) return `--${month}-${day}`;
    return `${year || ""}-${month || ""}-${day || ""}`;
  }

  if (!day && !month) return "";
  if (day && month) return `--${month}-${day}`;
  return `--${month || ""}-${day || ""}`;
};

export const DateInput = ({
  placeholder = "YYYY-MM-DD",
  name = "",
  isRequired = false,
  label = null,
  isDisabled = false,
  errorMessage = undefined,
  onChange = () => {},
  value = "",
  register = () => {},
  onKeyUp = () => {},
  classNames = "",
  isSplitDate = false,
  showYear = false,
  ...props
}) => {
  const [dateParts, setDateParts] = useState(parseDateValue(value));

  useEffect(() => {
    setDateParts(parseDateValue(value));
  }, [value]);

  const getDaysInMonth = (selectedMonth) => {
    if (!selectedMonth) return 31;
    if (selectedMonth === "02") return 29;
    if (["04", "06", "09", "11"].includes(selectedMonth)) return 30;
    return 31;
  };

  const handleSelectChange = (part, selectedValue) => {
    let nextDay = part === "day" ? selectedValue : dateParts.day;
    const nextMonth = part === "month" ? selectedValue : dateParts.month;
    const nextYear = part === "year" ? selectedValue : dateParts.year;

    if (part === "month") {
      const maxDays = getDaysInMonth(selectedValue);
      if (nextDay && Number(nextDay) > maxDays) {
        nextDay = "";
      }
    }

    const nextDateParts = {
      day: nextDay,
      month: nextMonth,
      year: nextYear,
    };
    const nextValue = combineDateValue(nextDateParts, showYear);

    setDateParts(nextDateParts);
    onChange({
      target: {
        name,
        value: nextValue,
      },
    });
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 1900 + 1 },
    (_, index) => currentYear - index,
  );
  const days = Array.from(
    { length: getDaysInMonth(dateParts.month) },
    (_, index) => String(index + 1).padStart(2, "0"),
  );
  const months = monthNames.map((month, index) => ({
    label: month,
    value: String(index + 1).padStart(2, "0"),
  }));

  if (isSplitDate) {
    return (
      <div className="date-input">
        {label !== null && (
          <label htmlFor={`${name}-month`} data-testid="label">
            {isRequired && <span className="text-danger">*</span>}
            <span>{label}</span>
          </label>
        )}

        <div className="date-input__picker" data-testid="input">
          <div className="date-input__split-field">
            <SelectField
              value={dateParts.month || ""}
              options={[
                { value: "", label: "Month" },
                ...months.map(({ value, label }) => ({ value, label })),
              ]}
              idKey="value"
              nameKey="label"
              placeholder="Month"
              isDisabled={isDisabled}
              isSearchable={false}
              onChange={(value) => handleSelectChange("month", value)}
            />
          </div>

          <div className="date-input__split-field">
            <SelectField
              value={dateParts.day || ""}
              options={[
                { value: "", label: "Day" },
                ...days.map((day) => ({ value: day, label: day })),
              ]}
              idKey="value"
              nameKey="label"
              placeholder="Day"
              isDisabled={isDisabled}
              isSearchable={false}
              onChange={(value) => handleSelectChange("day", value)}
            />
          </div>

          {showYear && (
            <div className="date-input__split-field">
              <SelectField
                value={dateParts.year || ""}
                options={[
                  { value: "", label: "Year" },
                  ...years.map((year) => ({
                    value: String(year),
                    label: String(year),
                  })),
                ]}
                idKey="value"
                nameKey="label"
                placeholder="Year"
                isDisabled={isDisabled}
                isSearchable={false}
                onChange={(value) => handleSelectChange("year", value)}
              />
            </div>
          )}
        </div>

        <input
          type="hidden"
          className={`${classNames} ${
            errorMessage !== undefined && "error-border"
          }`}
          name={name}
          id={name}
          value={combineDateValue(dateParts, showYear)}
          placeholder={placeholder}
          readOnly
          {...register(name, { required: isRequired })}
          {...props}
        />

        <p
          className={`error-message text-danger ${
            errorMessage !== undefined ? "show" : ""
          }`}
          data-testid="error-message"
        >
          {errorMessage !== undefined && (
            <motion.span initial={{ x: -10 }} animate={{ x: 0 }}>
              <FontAwesomeIcon
                icon={["fas", "exclamation-circle"]}
                className="mr-2"
                style={{ fontSize: "13px" }}
              />
              {errorMessage !== undefined && (
                <span>{errorMessage.message}</span>
              )}
            </motion.span>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="date-input">
      {label !== null && (
        <label htmlFor={name} data-testid="label">
          {isRequired && <span className="text-danger">*</span>}
          <span>{label}</span>
        </label>
      )}

      <input
        data-testid="input"
        type="date"
        className={`${classNames} ${
          errorMessage !== undefined && "error-border"
        }`}
        name={name}
        placeholder={placeholder}
        id={name}
        {...register(name, { required: isRequired })}
        onChange={(e) => {
          setDateParts(parseDateValue(e.target.value));
          onChange(e);
        }}
        disabled={isDisabled}
        onKeyUp={onKeyUp}
        value={value}
        {...props}
      />

      <p
        className={`error-message text-danger ${
          errorMessage !== undefined ? "show" : ""
        }`}
        data-testid="error-message"
      >
        {errorMessage !== undefined && (
          <motion.span initial={{ x: -10 }} animate={{ x: 0 }}>
            <FontAwesomeIcon
              icon={["fas", "exclamation-circle"]}
              className="mr-2"
              style={{ fontSize: "13px" }}
            />
            {errorMessage !== undefined && <span>{errorMessage.message}</span>}
          </motion.span>
        )}
      </p>
    </div>
  );
};
